/**
 * Form-state keys for the fields inside a selection-bound card.
 *
 * A canvas has one form state keyed by field path. A card that shows the selected row and a
 * personal field on another tab can bind the same field path, so sharing the key made one
 * overwrite or blank the other (SSI HR portal: tab 4 went blank because of tab 2's card). The
 * card's own fields get a namespaced key, so clearing or filling the card touches only the card.
 * The record the API returns and the payload it receives keep the plain field names.
 */
import type { RenderableElement } from "../components/canvasBuilder";
import { isWithinContainer, type ScopeNode } from "./canvasSelectionScope";

const CARD_FIELD_KEY_PREFIX = "selection-card:";

export const cardFieldKey = (path: string): string =>
  `${CARD_FIELD_KEY_PREFIX}${path}`;

export const isCardFieldKey = (key: string): boolean =>
  key.startsWith(CARD_FIELD_KEY_PREFIX);

/** The plain field name behind a state key, whichever kind of key it is. */
export const plainFieldPath = (key: string): string =>
  isCardFieldKey(key) ? key.slice(CARD_FIELD_KEY_PREFIX.length) : key;

/** The state values without the card's namespaced entries. */
export function nonCardValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !isCardFieldKey(key)),
  );
}

/** The card's state values under their plain field names. */
export function cardValuesByPlainPath(
  values: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([key]) => isCardFieldKey(key))
      .map(([key, value]) => [plainFieldPath(key), value]),
  );
}

/** The state values of the given card keys, read from a record by plain field name. */
export function cardValuesFromRecord(
  record: Record<string, unknown>,
  keys: ReadonlySet<string>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  keys.forEach((key) => {
    const path = plainFieldPath(key);
    if (Object.prototype.hasOwnProperty.call(record, path)) {
      values[key] = record[path];
    }
  });
  return values;
}

/**
 * Give every field and static element inside the selection card a namespaced field path, and
 * make the visibility conditions of EVERY element in the card (bound or not: the card itself,
 * nested containers, action buttons) follow the rename when they name one of the card's fields.
 * Elements outside the card, and canvases without a card, are returned unchanged: there the same
 * plain name is another field.
 */
export function namespaceCardFields(
  elements: RenderableElement[],
  scope: ScopeNode[],
  containerId: string | null,
): RenderableElement[] {
  if (!containerId) return elements;
  const inCard = (element: RenderableElement) =>
    isWithinContainer(element.id, containerId, scope);
  const cardPaths = new Set(
    elements
      .filter((element) => inCard(element) && element.data_binding?.field_path)
      .map((element) => element.data_binding!.field_path),
  );
  if (cardPaths.size === 0) return elements;
  return elements.map((element): RenderableElement => {
    if (!inCard(element)) return element;
    const binding = element.data_binding;
    const rules = element.visibilityRules;
    return {
      ...element,
      ...(binding?.field_path
        ? {
            data_binding: {
              ...binding,
              field_path: cardFieldKey(binding.field_path),
            },
          }
        : {}),
      ...(rules?.conditions
        ? {
            visibilityRules: {
              ...rules,
              conditions: rules.conditions.map((condition) =>
                cardPaths.has(condition.field)
                  ? { ...condition, field: cardFieldKey(condition.field) }
                  : condition,
              ),
            },
          }
        : {}),
    };
  });
}
